import { type Abi, type PublicClient } from 'viem'
import { type ContractName } from '@/config/contracts'

// Every first-party deployable implements the Chainlink-style `typeAndVersion()` getter returning
// "<ContractType> <semver>" (single space, type == Solidity contract name).
// The verifier probes a configured address once per (chainId, address) per
// session and classifies what actually lives there, so a stale or wrong
// address surfaces as a readable message instead of silent garbage reads.

export type ContractIdentityStatus =
    | { kind: 'ok'; type: string; version: string }
    | { kind: 'not-configured' } // no address for this (chain, contract) — routine in local dev, log only
    | { kind: 'no-code' } // address has no bytecode on this chain
    | { kind: 'not-identifiable' } // code present, typeAndVersion() reverted
    | { kind: 'type-mismatch'; found: string } // e.g. expected KYCRegistry, found UnipotDraw
    | { kind: 'version-mismatch'; found: string } // type ok, major version differs

export interface ExpectedIdentity {
    type: string // the Solidity contract name, e.g. 'KYCRegistry'
    majorVersion: number // mismatch warns, does not block
}

export const EXPECTED_IDENTITY: Record<ContractName, ExpectedIdentity> = {
    playerRegistry: { type: 'PlayerRegistry', majorVersion: 1 },
    kycRegistry: { type: 'KYCRegistry', majorVersion: 1 },
    operator: { type: 'BlockpotOperator', majorVersion: 1 },
    operatorRoles: { type: 'OperatorRoles', majorVersion: 1 },
    referralManager: { type: 'ReferralManager', majorVersion: 1 },
    potSeeder: { type: 'BlockpotPotSeeder', majorVersion: 1 },
    // Both games are UnipotDraw deployments from the neutral protocol.
    draw: { type: 'UnipotDraw', majorVersion: 1 },
    quickGame: { type: 'UnipotDraw', majorVersion: 1 },
}

const TYPE_AND_VERSION_ABI = [
    {
        type: 'function',
        name: 'typeAndVersion',
        inputs: [],
        outputs: [{ type: 'string' }],
        stateMutability: 'pure',
    },
] as const satisfies Abi

// One probe per (chainId, address) per session; the cached verdict also keeps
// re-mounts from re-toasting findings.
const cache = new Map<string, ContractIdentityStatus>()

export function clearIdentityCache() {
    cache.clear()
}

function classify(identity: string, expected: ExpectedIdentity): ContractIdentityStatus {
    const [type, version = ''] = identity.split(' ')
    if (type !== expected.type) return { kind: 'type-mismatch', found: identity }
    if (Number(version.split('.')[0]) !== expected.majorVersion)
        return { kind: 'version-mismatch', found: identity }
    return { kind: 'ok', type, version }
}

export async function verifyContractIdentity(
    client: PublicClient,
    address: `0x${string}` | undefined,
    expected: ExpectedIdentity,
): Promise<ContractIdentityStatus> {
    if (!address) return { kind: 'not-configured' }

    const key = `${client.chain?.id ?? 0}:${address.toLowerCase()}`
    const hit = cache.get(key)
    if (hit) return hit

    let status: ContractIdentityStatus
    const code = await client.getCode({ address })
    if (!code || code === '0x') {
        status = { kind: 'no-code' }
    } else {
        try {
            const identity = await client.readContract({
                address,
                abi: TYPE_AND_VERSION_ABI,
                functionName: 'typeAndVersion',
            })
            status = classify(identity as string, expected)
        } catch {
            status = { kind: 'not-identifiable' }
        }
    }
    cache.set(key, status)
    return status
}
